import type { CSSObject } from "@emotion/react";
import type { BoxProps } from "@ecco/ui/box";
import type { FlexProps } from "@ecco/ui/flex";
import type { TextProps } from "@ecco/ui/text";
import type { ButtonProps } from "@ecco/ui/button";
import { aspectRatios, breakpoints, colors, space } from "@ecco/ui/tokens";

const { lg } = breakpoints;

const mq = (minWidth: string) => `@media (min-width: ${minWidth})` as const;

/** Keeps the links and terms link aligned with the headline and description above them. */
const TEXT_MAX_WIDTH = "550px";

const TEXT_BLOCK_GAP = { base: space["8.8"], md: space["3"], lg: space["6.8"] };

const linkButtonBase = {
    fontWeight: "bold",
    height: "auto",
    display: "inline-flex",
    alignItems: "center",
    px: 0,
    textDecoration: "none",
    fontSize: { base: "14px", lg: "16px" },
    lineHeight: { base: "14px", lg: "16px" },
    _hover: {
        textDecoration: "none",
        color: colors.gray[500],
    },
} satisfies Partial<ButtonProps>;

export function getRootProps(isImageLeft: boolean, whiteBackground = false): FlexProps {
    return {
        width: "100%",
        boxSizing: "border-box",
        flexDirection: { base: "column", lg: isImageLeft ? "row-reverse" : "row" },
        alignItems: { base: "stretch", lg: "center" },
        gap: { base: space["4"], md: space["2.4"], lg: space["4.8"] },
        px: { base: "16px", lg: "32px" },
        py: { base: "32px", lg: "40px" },
        bg: whiteBackground ? colors.white : colors.offWhite,
    };
}

export function getHeadlineProps(isH1: boolean): BoxProps {
    return {
        color: colors.black,
        fontWeight: "bold",
        letterSpacing: "0.2px",
        margin: 0,
        fontSize: isH1 ? { base: "40px", lg: "56px" } : { base: "32px", lg: "40px" },
        lineHeight: isH1 ? { base: "48px", lg: "64px" } : { base: "38px", lg: "48px" },
    };
}

export const bannerProps = {
    content: {
        minWidth: { lg: 0 },
        flexDirection: "column",
        justifyContent: "center",
        width: { base: "100%", lg: "auto" },
        flex: { lg: "1 1 0" },
        gap: { base: space["1.6"], lg: space["6.8"] },
    } satisfies FlexProps,

    mainGroup: {
        width: "100%",
        maxWidth: TEXT_MAX_WIDTH,
        flexDirection: "column",
        justifyContent: "center",
        flex: { lg: 1 },
        gap: TEXT_BLOCK_GAP,
        order: { base: 1, lg: 0 },
    } satisfies FlexProps,

    textGroup: {
        maxWidth: TEXT_MAX_WIDTH,
        gap: space["1.6"],
        flexDirection: "column",
    } satisfies FlexProps,

    description: {
        color: colors.black,
        fontWeight: 400,
        letterSpacing: "0.4px",
        width: "100%",
        fontSize: { base: "16px", lg: "18px" },
        lineHeight: { base: "26px", lg: "28px" },
    } satisfies TextProps,

    links: {
        gap: "16px",
        flexWrap: "wrap",
        flexDirection: "row",
        alignItems: "flex-start",
    } satisfies FlexProps,

    termsLink: {
        width: "100%",
        maxWidth: TEXT_MAX_WIDTH,
        flexWrap: "wrap",
        flexDirection: "row",
        alignItems: "flex-start",
        order: { base: 3, lg: 0 },
    } satisfies FlexProps,

    termsLinkButton: {
        ...linkButtonBase,
        minHeight: space["4.8"],
        fontSize: { base: "13px", lg: "14px" },
        lineHeight: { base: "13px", lg: "14px" },
    } satisfies Partial<ButtonProps>,

    linkButton: {
        ...linkButtonBase,
        minHeight: space["11.2"],
    } satisfies Partial<ButtonProps>,

    linkLabel: {
        as: "span",
        display: "inline-block",
        borderBottom: `1px solid ${colors.black}`,
        paddingTop: "4px",
        paddingBottom: "4px",
        lineHeight: 1,
    } satisfies BoxProps,

    imageWrapper: {
        position: "relative",
        order: { base: 2, lg: 0 },
        minWidth: { lg: 0 },
        width: { base: "100%", lg: "auto" },
        overflow: "hidden",
        flex: { base: "0 0 auto", lg: "2 1 0" },
        height: "auto",
        aspectRatio: {
            base: aspectRatios["1:1"],
            sm: aspectRatios["4:3"],
            lg: aspectRatios["16:9"],
        },
    } satisfies BoxProps,
};

export function getContentProps(hasTermsLink: boolean): FlexProps {
    if (!hasTermsLink) {
        return bannerProps.content;
    }

    return {
        ...bannerProps.content,
        display: { base: "contents", lg: "flex" },
        alignSelf: { base: "auto", lg: "stretch" },
    };
}

export function getTextBlockAlignSelf(isImageLeft: boolean): FlexProps["alignSelf"] {
    return { base: isImageLeft ? "flex-end" : "flex-start", lg: "stretch" };
}

export const bannerCss = {
    mobileImage: {
        display: "block",
        [mq(lg)]: { display: "none" },
    } satisfies CSSObject,

    desktopImageWithMobile: {
        display: "none",
        [mq(lg)]: { display: "block" },
    } satisfies CSSObject,

    linkButtonHover: {
        "&:hover > *": {
            borderColor: colors.gray[500],
        },
    } satisfies CSSObject,
};
